import { CartEvents, cartEventEmitter } from "@made-people/grease-cart";
import { useEffect } from "react";
import { useMmGtm } from "../mm-gtm-context";
import { gtmGetCartItem } from "../mm-gtm-helpers";

export const MmGtmEventAddToCart = () => {
  const { pusher, frontendContext } = useMmGtm();

  useEffect(() => {
    /**
     * Add To Cart event
     */
    const onAddToCart = (params: CartEvents["addToCart"]) => {
      const addedItem = params.cart.items.find(
        (item) => item.id === params.payload.itemId,
      );
      if (!addedItem) {
        console.warn(
          `GTM Event Add To Cart: No item with ID '${params.payload.itemId}' found in cart, not pushing event`,
          { items: params.cart.items },
        );
        return;
      }

      pusher({
        event: "addToCart",
        eventLabel: addedItem.product.base.content.name,
        ecommerce: {
          add: {
            products: [
              gtmGetCartItem(
                addedItem,
                params.payload.quantity ?? 1,
                frontendContext,
              ),
            ],
          },
          cartItems: {
            products: params.cart.items.map((item) =>
              gtmGetCartItem(item, item.quantity, frontendContext),
            ),
          },
          order: {
            totalWithShipping: params.cart.totals?.grandTotal.number,
            totalWithoutShipping:
              (params.cart.totals?.subtotal.number ?? 0) +
              (params.cart.totals?.discount?.number ?? 0),
            checkoutUrl:
              frontendContext.baseUrl +
              "/" +
              frontendContext.siteSlug +
              "/checkout",
          },
        },
      });
    };

    /**
     * Update Quantity event, passes only the updated item if the
     * quantity is positive
     */
    const onUpdatedCart = (params: CartEvents["updateQuantity"]) => {
      const oldQuantity = params.oldCart?.items.find(
        (item) => item.lineId === params.payload.lineId,
      )?.quantity;
      if (oldQuantity === undefined) {
        return;
      }

      const updatedItem = params.cart.items.find(
        (item) => item.lineId === params.payload.lineId,
      );
      if (!updatedItem) {
        console.warn(
          `GTM Event Update Quantity: No item with ID '${params.payload.lineId}' found in cart, not pushing event`,
          { items: params.cart.items },
        );
        return;
      }

      const qtyDiff = params.payload.quantity - oldQuantity;
      if (qtyDiff > 0) {
        onAddToCart({
          cart: params.cart,
          payload: {
            itemId: updatedItem.id,
            quantity: qtyDiff,
          },
          // Type cast is fine because we don't use product in the event above
        } as CartEvents["addToCart"]);
      }
    };

    cartEventEmitter.on("addToCart", onAddToCart);
    cartEventEmitter.on("updateQuantity", onUpdatedCart);
    return () => {
      cartEventEmitter.off("addToCart", onAddToCart);
      cartEventEmitter.off("updateQuantity", onUpdatedCart);
    };
  }, []);

  return null;
};
